import React from 'react';
import { Sparkles, Compass, Crown, Heart } from 'lucide-react';

/**
 * AIBadge — Badge réutilisable identifiant le copilote IA Homly d'une fonctionnalité.
 * Branding interne : on n'affiche jamais le nom des modèles sous-jacents — uniquement
 * les personas "Pilote" (Manager), "Coach" (Agent) et "Confident" (Vendeur).
 *
 * Usage:
 *   <AIBadge persona="pilote" />
 *   <AIBadge persona="coach" size="sm" />
 *   <AIBadge persona="confident" label="Espace vendeur" />
 */
const PERSONA_META = {
    pilote: {
        short: 'Pilote',
        icon: Crown,
        gradient: 'from-[#681793] to-[#A855F7]',
        textColor: 'text-white',
        ring: 'ring-[#A855F7]/30',
    },
    coach: {
        short: 'Coach',
        icon: Compass,
        gradient: 'from-[#FF914D] to-[#FBB07A]',
        textColor: 'text-white',
        ring: 'ring-[#FF914D]/30',
    },
    confident: {
        short: 'Confident',
        icon: Heart,
        gradient: 'from-[#10B981] to-[#34D399]',
        textColor: 'text-white',
        ring: 'ring-[#10B981]/30',
    },
};

// Compat : d'anciens appels passaient `model="claude"|"gemini"`.
// On ne surface plus les modèles → on retombe sur "Pilote" par défaut.
const MODEL_TO_PERSONA = { claude: 'pilote', gemini: 'pilote' };

export default function AIBadge({
    persona,
    model,
    label = null,
    size = 'md',
    pulse = false,
    className = '',
}) {
    const key = persona || MODEL_TO_PERSONA[model] || 'pilote';
    const meta = PERSONA_META[key] || PERSONA_META.pilote;
    const Icon = meta.icon;

    const sizeStyles = {
        sm: 'text-[9px] py-0.5 px-2 gap-1',
        md: 'text-[10px] py-1 px-2.5 gap-1.5',
        lg: 'text-xs py-1.5 px-3 gap-2',
    };
    const iconSize = {
        sm: 'w-2.5 h-2.5',
        md: 'w-3 h-3',
        lg: 'w-3.5 h-3.5',
    };

    return (
        <span
            className={`inline-flex items-center font-bold uppercase tracking-wider rounded-full bg-gradient-to-r ${meta.gradient} ${meta.textColor} ${sizeStyles[size]} shadow-sm ring-1 ${meta.ring} ${className}`}
            data-testid={`ai-badge-${key}`}
            title={`Copilote IA Homly · ${meta.short}`}
        >
            <Icon className={`${iconSize[size]} shrink-0`} strokeWidth={1.5} />
            <span className="whitespace-nowrap">
                {label || `IA ${meta.short}`}
            </span>
            {pulse && (
                <span className="relative flex items-center justify-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                    <span className="absolute w-1.5 h-1.5 rounded-full bg-green-400 animate-ping" />
                </span>
            )}
        </span>
    );
}

/**
 * AIPoweredText — marqueur inline discret
 */
export function AIPoweredText({ persona = 'pilote', model, className = '' }) {
    const key = persona || MODEL_TO_PERSONA[model] || 'pilote';
    const meta = PERSONA_META[key] || PERSONA_META.pilote;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] text-white/40 font-medium ${className}`}>
            <Sparkles className="w-2.5 h-2.5 text-[#FF914D]" />
            <span>Propulsé par <span className="text-white/70">{meta.short}</span></span>
        </span>
    );
}
